<template>
  <BaseModal id="modalFilterDateTime" title="Date and Time">
    <div>
      <b-form-group>
        <b-form-radio
          v-model="selectedCondition"
          class="mt-3 mb-1"
          name="this-week"
          value="this-week"
        >
          Starting this week or later
        </b-form-radio>
        <b-form-radio v-model="selectedCondition" class="my-3" name="next-week" value="next-week">
          Starting next week or later
        </b-form-radio>
        <b-form-radio v-model="selectedCondition" class="my-3" name="next-month" value="next-month">
          Starting next month or later
        </b-form-radio>
        <b-form-radio
          v-model="selectedCondition"
          class="mt-1 mb-3"
          name="specific"
          value="specific"
        >
          Specific date and time
        </b-form-radio>
      </b-form-group>
    </div>

    <div class="form-group">
      <label>Start after</label>
      <span v-if="selectedCondition === 'specific' && start.date === null" class="ml-2">
        <small>(Please select start date)</small>
      </span>
      <b-row class="mb-4">
        <b-col cols="4" class="pr-0">
          <datepicker
            id="start-date"
            v-model="start.date"
            placeholder="Date"
            calendar-class="datepicker datepicker-dropdown dropdown-menu"
            :disabled="selectedCondition !== 'specific'"
            :bootstrap-styling="true"
          />
        </b-col>
        <b-col class="pl-3 pr-0">
          <b-form-select
            id="start-date-hour"
            v-model="start.hr"
            :disabled="selectedCondition !== 'specific'"
            :options="hrs"
          />
        </b-col>
        <b-col class="pl-3 pr-0">
          <b-form-select
            id="start-date-min"
            v-model="start.min"
            :disabled="selectedCondition !== 'specific'"
            :options="mins"
          />
        </b-col>
        <b-col class="pl-3">
          <b-form-select
            id="start-date-ampm"
            v-model="start.meridiem"
            :disabled="selectedCondition !== 'specific'"
            :options="['am', 'pm']"
          />
        </b-col>
      </b-row>
      <label>End by</label>
      <b-row class="mb-4">
        <b-col cols="4" class="pr-0">
          <datepicker
            id="end-date"
            v-model="end.date"
            placeholder="Date"
            calendar-class="datepicker datepicker-dropdown dropdown-menu"
            :disabled="selectedCondition !== 'specific'"
            :disabled-dates="disabledDateCondition"
            :bootstrap-styling="true"
          />
        </b-col>
        <b-col class="pl-3 pr-0">
          <b-form-select
            id="end-date-hour"
            v-model="end.hr"
            :disabled="selectedCondition !== 'specific'"
            :options="hrs"
          />
        </b-col>
        <b-col class="pl-3 pr-0">
          <b-form-select
            id="end-date-min"
            v-model="end.min"
            :disabled="selectedCondition !== 'specific'"
            :options="mins"
          />
        </b-col>
        <b-col class="pl-3">
          <b-form-select
            id="end-date-ampm"
            v-model="end.meridiem"
            :disabled="selectedCondition !== 'specific'"
            :options="['am', 'pm']"
          />
        </b-col>
      </b-row>
    </div>

    <template slot="modal-footer">
      <b-button variant="link" class="btn-link-dark" @click="hide()"> Cancel </b-button>
      <b-button
        :disabled="
          !selectedCondition ||
          (selectedCondition === 'specific' && start.date === null) ||
          isStartMoreThanEnd
        "
        variant="success"
        @click="submitFilterDateTime()"
      >
        Apply
      </b-button>
    </template>
  </BaseModal>
</template>

<script>
import { datetimeMixin } from '@/mixins';

import Datepicker from 'vuejs-datepicker';
import moment from 'moment';

import BaseModal from '@/components/base-components/BaseModal';

export default {
  name: 'ModalFilterDateTime',

  components: {
    Datepicker,
    BaseModal
  },

  mixins: [datetimeMixin],

  props: {
    /**
     * filterDateTime that emit to show in result
     *
     * ใช้ในการ Set date time ตอนเปิด modal ขึ้นมาใหม่
     */
    filterDateTime: {
      type: Object,
      default: () => ({
        start: null,
        end: null
      })
    },
    /**
     * Be true when click 'Reset all'
     */
    resetModal: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      /**
       * TEMPORARY > identify type of modal
       *
       * @type {String}
       */
      modalType: null,

      /**
       * Condition that determine to filter by range of date time
       *
       * @type {String}
       */
      selectedCondition: '',

      /**
       * filter by specific data with start and end date time
       *
       * @type {Object}
       */
      start: {
        date: null,
        hr: '08',
        min: '00',
        meridiem: 'am'
      },
      end: {
        date: null,
        hr: '08',
        min: '00',
        meridiem: 'am'
      },

      /**
       *  เก็บ value ที่ apply ไปแล้ว
       */
      appliedValue: null
    };
  },

  computed: {
    /**
     * listing times in hours unit
     *
     * @return {Array}
     */
    hrs() {
      let hrs = [];
      for (let i = 0; i < 12; i++) {
        let hr = i.toString();
        if (hr.length === 1) {
          hrs.push(`0${hr}`);
        } else {
          hrs.push(hr);
        }
      }
      return hrs;
    },

    /**
     * listing times in minutes unit
     *
     * @return {Array}
     */
    mins() {
      let mins = [];
      for (let i = 0; i < 60; i++) {
        let min = i.toString();
        if (min.length === 1) {
          mins.push(`0${i}`);
        } else {
          mins.push(min);
        }
      }
      return mins;
    },

    firstDayOfNextWeek() {
      return moment().startOf('week').add(1, 'weeks').format();
    },

    nextMonth() {
      return moment().add(1, 'months').startOf('month').format();
    },

    determineFilterDateTime() {
      switch (this.selectedCondition) {
        case 'this-week':
          return {
            start: this.today('format'),
            end: null
          };
        case 'next-week':
          return {
            start: this.firstDayOfNextWeek,
            end: null
          };
        case 'next-month':
          return {
            start: this.nextMonth,
            end: null
          };
        case 'specific':
          return {
            start: this.start.date
              ? moment(
                  `${moment(this.start.date).format('MM/DD/YYYY')} ${this.start.hr}:${
                    this.start.min
                  } ${this.start.meridiem}`
                ).format()
              : null,
            end: this.end.date
              ? moment(
                  `${moment(this.end.date).format('MM/DD/YYYY')} ${this.end.hr}:${this.end.min} ${
                    this.end.meridiem
                  }`
                ).format()
              : null
          };
        default:
          return null;
      }
    },

    /**
     * Condition to disabled date-picker
     *
     * document: https://www.npmjs.com/package/vuejs-datepicker
     */
    disabledDateCondition() {
      return {
        to:
          this.determineFilterDateTime && this.determineFilterDateTime.start
            ? new Date(this.determineFilterDateTime.start)
            : null
      };
    },

    /**
     * If start date > or = end date return true
     *
     * Which means user selects start date after end date
     *
     * @return {Boolean}
     */
    isStartMoreThanEnd() {
      if (!this.start.date || !this.end.date) return;

      if (this.determineFilterDateTime.start >= this.determineFilterDateTime.end) {
        return true;
      } else {
        return false;
      }
    }
  },

  watch: {
    /**
     * Reset modal when resetModal = true
     */
    resetModal: {
      handler(resetModal) {
        if (resetModal) {
          this.appliedValue = null;
          this.resetData();
        }
      }
    },

    selectedCondition: {
      handler() {
        this.start = {
          date: null,
          hr: '08',
          min: '00',
          meridiem: 'am'
        };
        this.end = {
          date: null,
          hr: '08',
          min: '00',
          meridiem: 'am'
        };
      }
    }

    /**
     * Watch if select end date before start date, it will set end date to null
     */
    // determineFilterDateTime() {
    //   if (!this.start.date || !this.end.date) return;

    //   console.log(
    //     this.determineFilterDateTime.start >= this.determineFilterDateTime.end
    //   );

    //   if (
    //     this.determineFilterDateTime.start >= this.determineFilterDateTime.end
    //   ) {
    //     console.log("true");
    //   }

    // if (this.start.date.getTime() > this.end.date.getTime()) {
    //   this.end.date = null;
    // }
    // },
  },

  methods: {
    /**
     * Open modal
     */
    show(type) {
      this.modalType = type;
      this.$bvModal.show('modalFilterDateTime');

      /**
       * if applied value === null
       *
       * แสดงว่ายังไม่ได้มีการกด apply จะ reset ข้อมูลใหม่หมด
       */
      if (this.appliedValue === null) {
        this.resetData();
      }

      /**
       * if selectedCondition === appliedValue
       *
       * It means already apply but didn't change filter when close modal
       */
      if (this.selectedCondition === this.appliedValue) {
        return;
      }

      /**
       * if selectedCondition !== this.appliedValue
       *
       * It means before close modal user change filter but didn't apply new one
       *
       * So, selectedCondition will remain the same when open modal
       */
      if (this.selectedCondition !== this.appliedValue) {
        this.selectedCondition = this.appliedValue;
      }

      /**
       * when selectedCondition === "specific" is applied => open modal
       *
       * => change filter => close modal => open again
       */
      if (
        this.appliedValue &&
        this.appliedValue.selectedCondition === 'specific' &&
        this.appliedValue.selectedCondition !== this.selectedCondition
      ) {
        /**
         * Set selectedCondition to applied value ("specific")
         */
        this.selectedCondition = this.appliedValue.selectedCondition;
        /**
         * Set every data by previous applied value
         */
        setTimeout(() => {
          this.start = this.appliedValue.start;
          this.start.date = new Date(this.filterDateTime.start);
          this.end = this.appliedValue.end;
          if (!this.filterDateTime.end) {
            this.end.date = null;
          } else {
            this.end.date = new Date(this.filterDateTime.end);
          }
        }, 0);
      }
    },

    hide() {
      this.$bvModal.hide('modalFilterDateTime');
    },

    /**
     * Send data back to parent component
     */
    submitFilterDateTime() {
      /**
       * Before submit, keep data in appliedValue
       *
       * Use to compare when user open modal again
       */
      this.appliedValue = this.selectedCondition;

      if (this.selectedCondition === 'specific') {
        this.appliedValue = {
          selectedCondition: 'specific',
          start: this.start,
          end: this.end
        };
      }

      this.$emit('confirmedFilter', this.determineFilterDateTime);

      this.$bvModal.hide('modalFilterDateTime');
    },

    /**
     * Reset data when resetModal = true
     */
    resetData() {
      this.selectedCondition = '';
      this.start = {
        date: null,
        hr: '08',
        min: '00',
        meridiem: 'am'
      };
      this.end = {
        date: null,
        hr: '08',
        min: '00',
        meridiem: 'am'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .datepicker header .prev {
  margin-top: 12px;
  margin-bottom: 12px;
  width: 28px;
  margin-right: 8px;
  height: 16px;
  content: url('../../../assets/chevron-left-solid.svg') !important;
}

::v-deep .datepicker header .next {
  margin-top: 12px;
  margin-bottom: 12px;
  width: 28px;
  margin-right: 8px;
  height: 16px;
  content: url('../../../assets/chevron-right-solid.svg') !important;
}
</style>
